import React, { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { captureException } from '../../utils/errorReporting';

const RELOAD_KEY = 'chunk-load-reload-attempted';

export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = String(err.message || '');
  return (
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg)
  );
}

// If we land here with a chunk-load error (e.g. lazyWithRetry already
// tried once and reload still failed, or a chunk import happened
// outside React.lazy), do one more best-effort reload before showing
// the fallback UI. The session-storage guard in lazyWithRetry prevents
// an infinite loop because it only allows one reload per session.
function reloadOnceForChunkError(error) {
  if (!isChunkLoadError(error)) return;
  try {
    const tried = window.sessionStorage.getItem(RELOAD_KEY) === '1';
    if (!tried) {
      window.sessionStorage.setItem(RELOAD_KEY, '1');
      window.location.reload();
    }
  } catch (_) { /* ignore storage errors */ }
}

function handleReload() {
  try { window.sessionStorage.removeItem(RELOAD_KEY); } catch (_) {}
  window.location.reload();
}

function ErrorFallback({ error, onRetry }) {
  const chunkError = isChunkLoadError(error);
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', padding: '2rem', fontFamily: 'system-ui, sans-serif', textAlign: 'center',
    }}>
      <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>
        {chunkError ? 'A new version is available' : 'Something went wrong'}
      </h1>
      <p style={{ color: '#666', marginBottom: '1.5rem', maxWidth: '400px' }}>
        {chunkError
          ? 'Please reload to load the latest version of the app.'
          : 'An unexpected error occurred. Please try refreshing the page.'}
      </p>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={handleReload}
          style={{
            padding: '0.5rem 1.25rem', borderRadius: '6px', border: 'none',
            background: '#8B5E3C', color: '#fff', cursor: 'pointer', fontSize: '0.875rem',
          }}
        >
          Reload page
        </button>
        {!chunkError && onRetry && (
          <button
            onClick={onRetry}
            style={{
              padding: '0.5rem 1.25rem', borderRadius: '6px', border: '1px solid #ddd',
              background: '#fff', cursor: 'pointer', fontSize: '0.875rem',
            }}
          >
            Try again
          </button>
        )}
        <button
          onClick={() => { window.location.href = '/'; }}
          style={{
            padding: '0.5rem 1.25rem', borderRadius: '6px', border: '1px solid #ddd',
            background: '#fff', color: '#1a1a1a', cursor: 'pointer', fontSize: '0.875rem',
          }}
        >
          Go home
        </button>
      </div>
    </div>
  );
}

/**
 * Router `errorElement`. React Router catches render errors inside
 * <RouterProvider> itself, so without this they never reach ErrorBoundary
 * and the user sees the raw "Unexpected Application Error!" page.
 */
export function RouteErrorFallback() {
  const error = useRouteError();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Route error:', error);
    }
    captureException(error, { source: 'routeErrorElement' });
    reloadOnceForChunkError(error);
  }, [error]);

  return <ErrorFallback error={error} />;
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('ErrorBoundary caught:', error, info);
    }
    captureException(error, { componentStack: info?.componentStack });
    reloadOnceForChunkError(error);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}
